import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Textarea,
  VStack,
  Text,
  useToast,
  Box,
  Spinner,
  HStack,
  Select,
  FormControl,
  FormLabel
} from '@chakra-ui/react';
import { ArrowForwardIcon, SettingsIcon } from '@chakra-ui/icons';
import api from '../../services/api';

const AIPromptModal = ({ isOpen, onClose, onSuccess }) => {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [provider, setProvider] = useState('gemini'); // Default to Gemini
  const toast = useToast();

  const handleSend = async () => {
    if (!prompt.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a prompt',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/api/v1/ai/generate-floorplan', {
        prompt: prompt.trim(),
        provider: provider
      });

      const data = response.data;
      
      if (data.success && data.objects) {
        // Pass the generated objects to the parent component
        onSuccess(data.objects);
        
        toast({
          title: 'Success',
          description: `Generated ${data.objects.length} objects using ${data.provider || provider}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Reset form and close modal
        setPrompt('');
        setProvider('gemini'); // Reset to default
        onClose();
      } else {
        throw new Error(data.error || 'Invalid response from AI');
      }
    } catch (error) {
      console.error('Error generating floor plan:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate floor plan',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setPrompt('');
      setProvider('gemini'); // Reset to default
      onClose();
    }
  };

  const examplePrompts = [
    "I want 2 rooms with windows in each wall and products in each window",
    "I want 3 rooms, the first one will have windows in 3 of the 4 walls",
    "Create a simple office layout with 1 room, 2 windows on the north wall, and 1 door on the south wall",
    "Design a residential floor plan with 2 bedrooms, each bedroom should have 1 window and products for shades"
  ];

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" closeOnOverlayClick={!isLoading}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <SettingsIcon color="blue.500" />
            <Text>AI Floor Plan Generator</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton isDisabled={isLoading} />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="semibold">AI Provider:</FormLabel>
              <Select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                isDisabled={isLoading}
                focusBorderColor="blue.500"
              >
                <option value="openai">OpenAI GPT-4.1</option>
                <option value="gemini">Google Gemini 2.5 Pro</option>
              </Select>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Choose which AI model to use for floor plan generation
              </Text>
            </FormControl>
            
            <Box>
              <Text mb={2} fontWeight="semibold" fontSize="sm">
                Describe your floor plan:
              </Text>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the rooms, windows, doors, and products you want..."
                rows={4}
                resize="vertical"
                isDisabled={isLoading}
                focusBorderColor="blue.500"
              />
            </Box>
            
            <Box>
              <Text mb={2} fontWeight="semibold" fontSize="sm" color="gray.600">
                Example prompts:
              </Text>
              <VStack spacing={2} align="stretch">
                {examplePrompts.map((example, index) => (
                  <Box
                    key={index}
                    p={2}
                    bg="gray.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                    cursor={isLoading ? "default" : "pointer"}
                    onClick={() => !isLoading && setPrompt(example)}
                    _hover={!isLoading ? { bg: "gray.100", borderColor: "blue.300" } : {}}
                    transition="all 0.2s"
                  >
                    <Text fontSize="sm" color="gray.700">
                      "{example}"
                    </Text>
                  </Box>
                ))}
              </VStack>
            </Box>
            
            <Box p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
              <Text fontSize="xs" color="blue.700">
                <strong>Tips:</strong> Be specific about room count, window/door placement, and product requirements. 
                The AI will generate 2D coordinates for rooms, windows, doors, and shade products.
                {provider === 'openai' && (
                  <Text as="span" display="block" mt={1} fontWeight="semibold">
                    Using OpenAI GPT-4.1 for enhanced accuracy and JSON formatting.
                  </Text>
                )}
                {provider === 'gemini' && (
                  <Text as="span" display="block" mt={1} fontWeight="semibold">
                    Using Google Gemini 2.5 Pro for advanced multimodal understanding.
                  </Text>
                )}
              </Text>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSend}
            isLoading={isLoading}
            loadingText="Generating..."
            leftIcon={!isLoading ? <ArrowForwardIcon /> : <Spinner size="sm" />}
            isDisabled={!prompt.trim() || isLoading}
          >
            Generate Floor Plan
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AIPromptModal;
